import dayjs from "dayjs";
import { Chip } from "@mui/material";
import { FaCalendarAlt, FaDollarSign, FaUser, FaTools, FaPen, FaTrashAlt } from "react-icons/fa";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiTooltip from "components/VuiTooltip";
import usePermissions from "hooks/usePermissions";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

const { dark, primary, light } = colors;

const priorityColors = {
  Low: "#94a3b8",
  Medium: "#3b82f6",
  High: "#f59e0b",
  Urgent: "#ef4444",
};

const statusColors = {
  Pending: "#f59e0b",
  Scheduled: "#3b82f6",
  InProgress: "#8b5cf6",
  "In Progress": "#8b5cf6",
  Completed: "#22c55e",
};

function MaintenanceCard({ task, onView, onEdit, onDelete }) {
  const { can } = usePermissions();

  const getAssignedStaffName = (staff) => staff?.name || staff?.fullName || staff?.email;

  const assignedStaff = Array.isArray(task?.assignedStaff)
    ? (() => {
        const staffNames = task.assignedStaff.map(getAssignedStaffName).filter(Boolean);

        if (!staffNames.length) return "Unassigned";

        if (staffNames.length > 2) {
          return `${staffNames.slice(0, 2).join(", ")} +${staffNames.length - 2}`;
        }

        return staffNames.join(", ");
      })()
    : task?.assignedStaff?.name ||
      task?.assignedStaff?.fullName ||
      task?.assignedStaff ||
      "Unassigned";

  const formattedDate = task?.scheduledDate
    ? dayjs(task.scheduledDate.split("T")[0]).format("MMM DD, YYYY")
    : "Not Scheduled";

  return (
    <VuiBox
      p={2.5}
      borderRadius="20px"
      display="flex"
      flexDirection="column"
      sx={{
        background: "rgba(255,255,255,0.03)",
        backdropFilter: "blur(20px)",
        border: `1px solid ${rgba(dark.main, 0.5)}`,
        transition: "all 0.25s ease",
        minHeight: "320px",

        "&:hover": {
          border: `1px solid ${rgba(primary.main, 0.4)}`,
          transform: "translateY(-4px)",
        },
      }}
    >
      {/* Header */}
      <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <VuiBox flex={1} mr={2}>
          <VuiTypography
            fontWeight="medium"
            sx={{
              fontSize: "18px",
              lineHeight: 1.3,
              display: "block",
              mb: 0.5,
            }}
          >
            {task?.taskTitle}
          </VuiTypography>

          <VuiTypography
            variant="caption"
            sx={{
              display: "block",
            }}
          >
            {task?.roomArea}
          </VuiTypography>
        </VuiBox>

        <Chip
          size="small"
          label={task?.priority}
          sx={{
            background: `${priorityColors[task?.priority]}20`,
            color: priorityColors[task?.priority],
            border: `1px solid ${priorityColors[task?.priority]}50`,
            fontWeight: 400,
            fontSize: "11px",
            flexShrink: 0,
          }}
        />
      </VuiBox>

      {/* Description */}
      <VuiTypography
        variant="caption"
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          mb: 1,
        }}
      >
        {task?.description}
      </VuiTypography>

      {/* Category */}
      <VuiBox mb={1}>
        <Chip
          size="small"
          icon={<FaTools size={10} sx={{ color: primary.main }} />}
          label={task?.category}
          sx={{
            background: light.secondary,
            color: primary.main,
            px: 1,
          }}
        />
      </VuiBox>

      {/* Status */}
      <VuiBox mb={1}>
        <VuiTypography
          variant="caption"
          sx={{
            color: statusColors[task?.status] || "#94a3b8",
            fontWeight: 500,
          }}
        >
          {task?.status}
        </VuiTypography>
      </VuiBox>

      {/* Meta */}
      <VuiBox display="flex" flexDirection="column" gap={1.5} mb={2}>
        <VuiBox display="flex" alignItems="center" gap={1}>
          <FaCalendarAlt size={12} sx={{ color: primary.main }} />

          <VuiTypography variant="caption">{formattedDate}</VuiTypography>
        </VuiBox>

        <VuiBox display="flex" alignItems="center" gap={1}>
          <FaDollarSign size={12} sx={{ color: primary.main }} />

          <VuiTypography variant="caption">
            ${Number(task?.estimatedRepairCost || 0).toLocaleString()}
          </VuiTypography>
        </VuiBox>

        <VuiBox display="flex" alignItems="center" gap={1}>
          <FaUser size={12} sx={{ color: primary.main }} />

          <VuiTypography variant="caption">{assignedStaff}</VuiTypography>
        </VuiBox>
      </VuiBox>

      {/* Footer */}
      <VuiBox mt="auto" display="flex" gap={1}>
        {can("deleteMaintenance") && (
          <VuiTooltip title="Delete Task">
            <VuiButton
              variant="outlined"
              color="error"
              size="small"
              sx={{
                minWidth: "36px",
                width: "36px",
                height: "36px",
                padding: 0,
                borderRadius: "10px",
                border: `1px solid ${rgba(dark.main, 0.5)}`,
                color: primary.main,

                "&:hover": {
                  color: "#ef4444",
                  background: "rgba(239,68,68,0.12)",
                  border: "1px solid rgba(239,68,68,0.35)",
                },
              }}
              onClick={() => onDelete?.(task)}
            >
              <FaTrashAlt size={12} />
            </VuiButton>
          </VuiTooltip>
        )}

        <VuiButton
          variant="outlined"
          size="small"
          color="primary"
          fullWidth
          onClick={() => onView?.(task)}
        >
          Details
        </VuiButton>

        {can("editMaintenance") && (
          <VuiButton
            variant="contained"
            color="primary"
            size="small"
            fullWidth
            onClick={() => onEdit?.(task)}
          >
            <FaPen size={11} style={{ marginRight: 6 }} />
            Edit
          </VuiButton>
        )}
      </VuiBox>
    </VuiBox>
  );
}

export default MaintenanceCard;
